import React, {
  useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef,
} from 'react';
import ReactDOM from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Eye, EyeOff, Trash2, Plus, ChevronDown, ChevronRight, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiPatch, apiPost, apiDelete } from '../../../api/client';
import type { ContractSection, SaveState, VarAutoItem } from './types';
import SaveIndicator from './SaveIndicator';

// ── Variable Autocomplete utilities ─────────────────────────────────────────

interface VarAutoItemLocal {
  key: string;
  desc: string;
  isCustom?: boolean;
}

function filterVars(items: VarAutoItemLocal[], filter: string): VarAutoItemLocal[] {
  const lf = filter.toLowerCase();
  if (!lf) return items.slice(0, 8);
  return items
    .filter(v => v.key.toLowerCase().includes(lf))
    .sort((a, b) => {
      const aS = a.key.toLowerCase().startsWith(lf) ? 0 : 1;
      const bS = b.key.toLowerCase().startsWith(lf) ? 0 : 1;
      return aS - bS;
    })
    .slice(0, 8);
}

function getCaretCoordinates(el: HTMLTextAreaElement, pos: number): { top: number; left: number } {
  const style = window.getComputedStyle(el);
  const div = document.createElement('div');
  const rect = el.getBoundingClientRect();
  (
    ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing',
      'lineHeight', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
      'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
      'boxSizing', 'wordBreak', 'overflowWrap'] as const
  ).forEach(p => { (div.style as any)[p] = style[p]; });
  div.style.position = 'fixed';
  div.style.visibility = 'hidden';
  div.style.overflow = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.top = `${rect.top - el.scrollTop}px`;
  div.style.left = `${rect.left - el.scrollLeft}px`;
  div.style.width = `${rect.width}px`;
  div.style.height = `${rect.height}px`;
  div.textContent = el.value.slice(0, pos);
  const span = document.createElement('span');
  span.textContent = '​';
  div.appendChild(span);
  document.body.appendChild(div);
  const spanRect = span.getBoundingClientRect();
  document.body.removeChild(div);
  const lineHeight = parseFloat(style.lineHeight) || 18;
  return { top: spanRect.top + lineHeight + 2, left: spanRect.left };
}

// ── Variable Autocomplete component ─────────────────────────────────────────

interface VariableAutocompleteProps {
  items: VarAutoItemLocal[];
  filter: string;
  position: { top: number; left: number };
  activeIndex: number;
  onSelect: (key: string) => void;
  onClose: () => void;
}

function VariableAutocomplete({ items, filter, position, activeIndex, onSelect, onClose }: VariableAutocompleteProps) {
  const filtered = filterVars(items, filter);

  useEffect(() => {
    if (filtered.length === 0) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-var-ac]')) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, filtered.length]);

  if (filtered.length === 0) return null;

  const itemHeight = 32;
  const dropdownH = filtered.length * itemHeight + 8;
  const flipUp = position.top + dropdownH > window.innerHeight - 8;

  const el = (
    <div
      data-var-ac=""
      style={{
        position: 'fixed',
        top: flipUp ? position.top - dropdownH - 20 : position.top,
        left: Math.min(position.left, window.innerWidth - 280),
        zIndex: 10000,
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        minWidth: 220,
        maxWidth: 320,
        overflow: 'hidden',
        fontSize: 12,
      }}
    >
      {filtered.map((v, i) => (
        <div
          key={v.key}
          onMouseDown={e => { e.preventDefault(); onSelect(v.key); }}
          style={{
            padding: '5px 12px',
            height: itemHeight,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: i === activeIndex ? 'var(--color-primary)' : 'transparent',
            color: i === activeIndex ? 'white' : 'var(--color-text)',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v.key}</span>
          <span style={{ fontSize: 10, opacity: 0.65, marginLeft: 8, flexShrink: 0 }}>
            {v.isCustom ? 'custom' : v.desc}
          </span>
        </div>
      ))}
    </div>
  );

  return ReactDOM.createPortal(el, document.body);
}

// ── Section Card (with imperative insert handle) ──────────────────────────────

export interface SectionCardHandle {
  insertAtCursor: (token: string) => void;
}

interface SectionCardProps {
  section: ContractSection;
  isAdmin: boolean;
  isActive: boolean;
  isExpanded: boolean;
  onSetActive: (id: string) => void;
  onToggleExpanded: (id: string) => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDrop: (e: React.DragEvent, id: string) => void;
  dragOverId: string | null;
  onBodyChange: (id: string, body: string) => void;
  onSaveStateChange: (id: string, state: SaveState) => void;
  saveState: SaveState;
  allVariables: VarAutoItemLocal[];
}

const SectionCard = forwardRef<SectionCardHandle, SectionCardProps>(function SectionCard(
  {
    section, isAdmin, isActive, isExpanded, onSetActive, onToggleExpanded,
    onDragStart, onDragOver, onDrop, dragOverId,
    onBodyChange, onSaveStateChange, saveState, allVariables,
  },
  ref,
) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(section.title);
  const [body, setBody] = useState(section.body);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [acOpen, setAcOpen] = useState(false);
  const [acActiveIndex, setAcActiveIndex] = useState(0);
  const acRef = useRef<{ filter: string; replaceStart: number; position: { top: number; left: number } }>({
    filter: '', replaceStart: 0, position: { top: 0, left: 0 },
  });

  useEffect(() => { setTitle(section.title); setBody(section.body); }, [section.title, section.body]);
  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);
  useEffect(() => {
    if (isExpanded && textareaRef.current) {
      const timer = setTimeout(() => { textareaRef.current?.focus(); }, 50);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  useImperativeHandle(ref, () => ({
    insertAtCursor: (token: string) => {
      const ta = textareaRef.current;
      if (!ta) return;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newBody = body.substring(0, start) + token + body.substring(end);
      handleBodyChange(newBody);
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(start + token.length, start + token.length);
      });
    },
  }));

  const triggerSave = useCallback((newTitle: string, newBody: string) => {
    onSaveStateChange(section.id, 'saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      apiPatch(`/contract-template/sections/${section.id}`, { title: newTitle, body: newBody })
        .then(() => {
          qc.invalidateQueries({ queryKey: ['contract-template'] });
          onSaveStateChange(section.id, 'saved');
          setTimeout(() => onSaveStateChange(section.id, 'idle'), 2000);
        })
        .catch((e: Error) => {
          toast.error(e.message || 'Errore salvataggio');
          onSaveStateChange(section.id, 'error');
        });
    }, 1200);
  }, [section.id, qc, onSaveStateChange]);

  const handleTitleChange = (v: string) => { setTitle(v); triggerSave(v, body); };
  const handleBodyChange = (v: string) => {
    setBody(v);
    onBodyChange(section.id, v);
    triggerSave(title, v);
  };

  const toggleMutation = useMutation({
    mutationFn: () => apiPatch(`/contract-template/sections/${section.id}`, { isVisible: !section.isVisible }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contract-template'] }),
    onError: (e: Error) => toast.error(e.message || 'Errore'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/contract-template/sections/${section.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contract-template'] }); toast.success('Sezione eliminata'); },
    onError: () => toast.error('Errore eliminazione'),
  });

  const duplicateMutation = useMutation({
    mutationFn: () => apiPost(`/contract-template/sections/${section.id}/duplicate`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contract-template'] }); toast.success('Sezione duplicata'); },
    onError: () => toast.error('Errore duplicazione sezione'),
  });

  const canEdit = isAdmin;
  const isOver = dragOverId === section.id;
  const snippet = body.replace(/[#*`\n]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 90);

  const cardBorderColor = isActive
    ? 'var(--color-primary)'
    : isOver
      ? 'var(--color-primary)'
      : 'rgba(255,255,255,0.10)';

  const wrapSelection = (wrapper: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = body.substring(start, end) || 'testo';
    const newBody = body.substring(0, start) + wrapper + sel + wrapper + body.substring(end);
    handleBodyChange(newBody);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + wrapper.length, start + wrapper.length + sel.length);
    });
  };

  const insertText = (text: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const newBody = body.substring(0, start) + text + body.substring(start);
    handleBodyChange(newBody);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + text.length, start + text.length);
    });
  };

  const handleSelectVar = useCallback((key: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const { replaceStart } = acRef.current;
    const curPos = ta.selectionStart;
    const token = `{{${key}}}`;
    const newBody = body.substring(0, replaceStart) + token + body.substring(curPos);
    handleBodyChange(newBody);
    setAcOpen(false);
    requestAnimationFrame(() => {
      ta.focus();
      const newPos = replaceStart + token.length;
      ta.setSelectionRange(newPos, newPos);
    });
  }, [body, handleBodyChange]);

  const handleInput = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const pos = ta.selectionStart;
    const text = ta.value;
    let start = -1;
    for (let i = pos - 1; i >= 1; i--) {
      if (text[i] === '{' && text[i - 1] === '{') { start = i - 1; break; }
      if (text[i] === '}' || text[i] === '\n') break;
    }
    if (start === -1) { setAcOpen(false); return; }
    const filter = text.slice(start + 2, pos);
    const coords = getCaretCoordinates(ta, pos);
    acRef.current = { filter, replaceStart: start, position: coords };
    setAcActiveIndex(0);
    setAcOpen(true);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!acOpen) return;
    const filtered = filterVars(allVariables, acRef.current.filter);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAcActiveIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAcActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[acActiveIndex]) handleSelectVar(filtered[acActiveIndex].key);
    } else if (e.key === 'Escape') {
      setAcOpen(false);
    }
  }, [acOpen, acActiveIndex, allVariables, handleSelectVar]);

  const openAutocompleteFromToolbar = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const newBody = body.substring(0, start) + '{{' + body.substring(start);
    handleBodyChange(newBody);
    setTimeout(() => {
      const ta2 = textareaRef.current;
      if (!ta2) return;
      ta2.focus();
      const newPos = start + 2;
      ta2.setSelectionRange(newPos, newPos);
      const coords = getCaretCoordinates(ta2, newPos);
      acRef.current = { filter: '', replaceStart: start, position: coords };
      setAcActiveIndex(0);
      setAcOpen(true);
    }, 50);
  }, [body, handleBodyChange]);

  const varCount = (body.match(/\{\{[^}]+\}\}/g) ?? []).length;

  return (
    <div
      draggable={canEdit}
      onDragStart={e => onDragStart(e, section.id)}
      onDragOver={e => onDragOver(e, section.id)}
      onDrop={e => onDrop(e, section.id)}
      style={{
        position: 'relative',
        border: `1.5px solid ${cardBorderColor}`,
        borderRadius: 'var(--radius-card)',
        background: isActive
          ? 'var(--color-primary-highlight, rgba(29,78,87,0.06))'
          : section.isVisible ? 'var(--glass-bg-card)' : 'var(--glass-bg-card)',
        opacity: section.isVisible ? 1 : 0.65,
        transition: 'border-color 0.12s, background 0.12s, box-shadow 0.12s',
        boxShadow: isActive ? '0 0 0 2px var(--color-primary)' : 'none',
      }}
    >
      {/* Drag handle strip */}
      {canEdit && (
        <div
          style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: '4px', background: 'transparent',
            cursor: 'grab', borderRadius: 'var(--radius-card) 0 0 var(--radius-card)',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--color-primary)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        />
      )}

      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.55rem 0.7rem',
          cursor: canEdit ? 'pointer' : 'default',
          borderBottom: isExpanded ? '1px solid rgba(255,255,255,0.08)' : 'none',
        }}
        onClick={() => {
          if (!canEdit) return;
          onToggleExpanded(section.id);
          if (!isExpanded) onSetActive(section.id);
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden' }}>
          {canEdit ? (
            <input
              value={title}
              onChange={e => { handleTitleChange(e.target.value); }}
              style={{
                border: 'none', background: 'transparent', fontWeight: 600,
                fontSize: 'var(--text-sm)', color: 'var(--color-text)',
                width: '100%', outline: 'none', padding: 0,
              }}
            />
          ) : (
            <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{title}</span>
          )}
        </span>
        <SaveIndicator state={saveState} />
        {canEdit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.1rem', flexShrink: 0 }}>
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); toggleMutation.mutate(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: section.isVisible ? 'var(--color-success)' : 'var(--color-text-faint)' }}
              title={section.isVisible ? 'Nascondi sezione' : 'Mostra sezione'}
            >
              {section.isVisible ? <Eye size={12} /> : <EyeOff size={12} />}
            </button>
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); duplicateMutation.mutate(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: 'var(--color-text-faint)' }}
              title="Duplica sezione"
            >
              <Copy size={12} />
            </button>
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); if (confirm('Eliminare questa sezione?')) deleteMutation.mutate(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: 'var(--color-danger)' }}
              title="Elimina sezione"
            >
              <Trash2 size={12} />
            </button>
            {isExpanded
              ? <ChevronDown size={12} style={{ color: 'var(--color-text-faint)' }} />
              : <ChevronRight size={12} style={{ color: 'var(--color-text-faint)' }} />}
          </div>
        )}
      </div>

      {/* Snippet (collapsed, canEdit) */}
      {!isExpanded && canEdit && snippet && (
        <div style={{
          padding: '0.35rem 0.7rem 0.6rem',
          fontSize: 11, color: 'var(--color-text-faint)',
          fontStyle: 'italic', lineHeight: 1.4,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {snippet}{body.length > 90 ? '…' : ''}
        </div>
      )}

      {/* Expanded editor */}
      {isExpanded && canEdit && (
        <div style={{ padding: '0.7rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {/* Toolbar */}
          <div style={{
            display: 'flex', gap: '0.25rem',
            padding: '0.35rem', background: 'var(--glass-bg-card)',
            borderRadius: 'var(--radius-interactive)', borderBottom: '1px solid rgba(255,255,255,0.10)',
          }}>
            <button
              onClick={() => wrapSelection('**')}
              title="Grassetto"
              style={{
                padding: '0.25rem 0.35rem', border: 'none',
                background: 'var(--glass-bg-card)', cursor: 'pointer',
                borderRadius: 'var(--radius-xs)', fontSize: '11px', fontWeight: 700,
                color: 'var(--color-text)',
              }}
            >
              B
            </button>
            <button
              onClick={() => wrapSelection('*')}
              title="Corsivo"
              style={{
                padding: '0.25rem 0.35rem', border: 'none',
                background: 'var(--glass-bg-card)', cursor: 'pointer',
                borderRadius: 'var(--radius-xs)', fontSize: '11px', fontStyle: 'italic',
                color: 'var(--color-text)',
              }}
            >
              I
            </button>
            <div style={{ width: '1px', background: 'rgba(255,255,255,0.10)' }} />
            <button
              onClick={() => insertText('---\n')}
              title="Separatore"
              style={{
                padding: '0.25rem 0.35rem', border: 'none',
                background: 'var(--glass-bg-card)', cursor: 'pointer',
                borderRadius: 'var(--radius-xs)', fontSize: '10px',
                color: 'var(--color-text)',
              }}
            >
              —
            </button>
            <button
              onClick={() => insertText('\n\n')}
              title="Paragrafo"
              style={{
                padding: '0.25rem 0.35rem', border: 'none',
                background: 'var(--glass-bg-card)', cursor: 'pointer',
                borderRadius: 'var(--radius-xs)', fontSize: '10px',
                color: 'var(--color-text)',
              }}
            >
              ¶
            </button>
            <div style={{ width: '1px', background: 'rgba(255,255,255,0.10)' }} />
            <button
              onMouseDown={e => e.preventDefault()}
              onClick={openAutocompleteFromToolbar}
              title="Inserisci variabile"
              style={{
                padding: '0.25rem 0.45rem', border: '1px solid var(--color-primary)',
                background: 'var(--color-primary-highlight, rgba(29,78,87,0.08))',
                cursor: 'pointer', borderRadius: 'var(--radius-xs)',
                fontSize: '11px', fontWeight: 700, color: 'var(--color-primary)',
                fontFamily: 'monospace',
              }}
            >
              {'{ }'}
            </button>
          </div>

          <textarea
            ref={textareaRef}
            value={body}
            onChange={e => handleBodyChange(e.target.value)}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onFocus={() => onSetActive(section.id)}
            onBlur={() => setTimeout(() => setAcOpen(false), 150)}
            rows={10}
            style={{
              width: '100%', resize: 'vertical', boxSizing: 'border-box',
              fontFamily: 'monospace', fontSize: '12px',
              padding: '0.55rem', borderRadius: 'var(--radius-interactive)',
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'var(--glass-bg-card)', color: 'var(--color-text)',
              lineHeight: 1.5, overflowWrap: 'break-word', wordBreak: 'normal',
            }}
            placeholder="Scrivi in Markdown… clicca una variabile nella sidebar per inserirla qui"
          />

          {acOpen && (
            <VariableAutocomplete
              items={allVariables}
              filter={acRef.current.filter}
              position={acRef.current.position}
              activeIndex={acActiveIndex}
              onSelect={handleSelectVar}
              onClose={() => setAcOpen(false)}
            />
          )}

          {/* Counter */}
          <div style={{ fontSize: 10, color: 'var(--color-text-faint)', textAlign: 'right', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <span>{body.length} caratteri</span>
            <span>{varCount} variabili</span>
          </div>

          <p style={{ fontSize: 10, color: 'var(--color-text-faint)', margin: 0, lineHeight: 1.4, overflowWrap: 'break-word' }}>
            Markdown supportato: **grassetto**, *corsivo*, # Titolo, - lista. Clicca una variabile nella barra sinistra per inserirla nel punto corrente.
          </p>
        </div>
      )}

      {/* Read-only body preview (non-admin) */}
      {!canEdit && body && (
        <div style={{ padding: '0.625rem 0.7rem', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 1.5, overflow: 'hidden' }}>
          {body.slice(0, 120)}{body.length > 120 ? '…' : ''}
        </div>
      )}
    </div>
  );
});

export default SectionCard;
